import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { 
  Package, 
  Truck, 
  Phone, 
  Mail, 
  MapPin, 
  Clock, 
  CheckCircle2, 
  XCircle,
  User,
  Store,
  Image as ImageIcon,
  AlertCircle,
  Loader2,
  Navigation
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Simple map component using iframe with Google Maps or OpenStreetMap
const DriverLocationMap = ({ driverLocation, deliveryAddress, driverName }) => {
  if (!driverLocation || !driverLocation.lat || !driverLocation.lng) {
    return (
      <div className="bg-slate-100 rounded-lg p-8 text-center">
        <MapPin className="w-12 h-12 text-slate-400 mx-auto mb-2" />
        <p className="text-slate-500">Driver location not available yet</p>
        <p className="text-xs text-slate-400 mt-1">Location will appear when driver starts the route</p>
      </div>
    );
  }

  const { lat, lng } = driverLocation;
  
  // Use OpenStreetMap embed (free, no API key needed)
  const mapUrl = `https://www.openstreetmap.org/export/embed.html?bbox=${lng-0.02}%2C${lat-0.02}%2C${lng+0.02}%2C${lat+0.02}&layer=mapnik&marker=${lat}%2C${lng}`;

  return (
    <div className="space-y-2">
      <div className="relative rounded-lg overflow-hidden border border-slate-200" style={{ height: '250px' }}>
        <iframe
          title="Driver Location"
          width="100%"
          height="100%"
          frameBorder="0"
          scrolling="no"
          src={mapUrl}
          style={{ border: 0 }}
        />
        {/* Overlay with driver info */}
        <div className="absolute bottom-2 left-2 bg-white/95 backdrop-blur-sm rounded-lg px-3 py-2 shadow-lg">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center">
              <Truck className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="text-xs font-medium text-slate-800">{driverName || 'Your Driver'}</p>
              <p className="text-xs text-slate-500">Live Location</p>
            </div>
          </div>
        </div>
      </div>
      <p className="text-xs text-center text-slate-400">
        Location updates every 30 seconds
      </p>
    </div>
  );
};

const DeliveryTrackingPage = () => {
  const { trackingToken } = useParams();
  const [tracking, setTracking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let abortController = new AbortController();
    let mounted = true;

    const fetchTracking = async () => {
      try {
        abortController.abort();
        abortController = new AbortController();

        const response = await fetch(`${API_URL}/api/tracking/${trackingToken}`, {
          signal: abortController.signal,
          cache: 'no-store',
        });
        
        // Read body as text first (safe — works for any response type)
        const text = await response.text();
        
        if (!mounted) return;
        
        if (!response.ok) {
          // Try to parse error detail from JSON body
          let detail = 'Tracking information not found';
          try {
            const parsed = JSON.parse(text);
            detail = parsed.detail || detail;
          } catch { /* non-JSON error response — use default message */ }
          throw new Error(detail);
        }
        
        const data = JSON.parse(text);
        setTracking(data);
        setError(null);
      } catch (err) {
        if (err.name === 'AbortError' || !mounted) return;
        setError(err.message);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    if (trackingToken) {
      fetchTracking();
      const interval = setInterval(fetchTracking, 30000);
      return () => {
        mounted = false;
        clearInterval(interval);
        abortController.abort();
      };
    }
  }, [trackingToken]);

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'Pending', color: 'bg-gray-100 text-gray-800', icon: Clock },
      in_transit: { label: 'In Transit', color: 'bg-blue-100 text-blue-800', icon: Truck },
      completed: { label: 'Delivered', color: 'bg-green-100 text-green-800', icon: CheckCircle2 },
      failed: { label: 'Failed', color: 'bg-red-100 text-red-800', icon: XCircle },
      skipped: { label: 'Skipped', color: 'bg-yellow-100 text-yellow-800', icon: AlertCircle },
      // v5.62 r33 — reserved for a courier, not yet confirmed (overrides the in-house status)
      hand_off_pending: { label: 'Handing to courier', color: 'bg-sky-100 text-sky-800', icon: Truck }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <Badge className={`${config.color} flex items-center gap-1 text-sm px-3 py-1`}>
        <Icon className="w-4 h-4" />
        {config.label}
      </Badge>
    );
  };

  const formatTime = (isoString) => {
    if (!isoString) return null;
    try {
      const date = new Date(isoString);
      return date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      });
    } catch {
      return isoString;
    }
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return null;
    try {
      const date = new Date(isoString);
      return date.toLocaleString('en-US', { 
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      });
    } catch {
      return isoString;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      const date = new Date(dateStr + 'T00:00:00');
      return date.toLocaleDateString('en-US', { 
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-slate-600">Loading tracking information...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-800 mb-2">Tracking Not Found</h2>
            <p className="text-slate-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Calculate ETA display
  const getETADisplay = () => {
    const delivery = tracking?.delivery;
    if (!delivery) return null;
    
    // Check for ETA window
    if (delivery.eta_window_start && delivery.eta_window_end) {
      return `${formatTime(delivery.eta_window_start)} - ${formatTime(delivery.eta_window_end)}`;
    }
    
    // Check for single ETA
    if (delivery.eta) {
      const etaTime = formatTime(delivery.eta);
      if (etaTime) return `Around ${etaTime}`;
    }
    
    // Check for estimated_arrival
    if (delivery.estimated_arrival) {
      return `Around ${formatTime(delivery.estimated_arrival)}`;
    }
    
    return null;
  };

  const etaDisplay = getETADisplay();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-8 px-4">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header with Store Branding */}
        <Card className="overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6 text-white">
            <div className="flex items-center gap-4">
              {tracking?.store?.logo_url ? (
                <img 
                  src={tracking.store.logo_url} 
                  alt={tracking.store.name}
                  className="w-16 h-16 rounded-lg bg-white p-1 object-contain"
                />
              ) : (
                <div className="w-16 h-16 rounded-lg bg-white/20 flex items-center justify-center">
                  <Store className="w-8 h-8" />
                </div>
              )}
              <div>
                <h1 className="text-2xl font-bold">{tracking?.store?.name || 'Delivery Tracking'}</h1>
                <p className="text-blue-100 text-sm">Track your delivery in real-time</p>
              </div>
            </div>
          </div>
          
          {/* Store Contact Info */}
          <CardContent className="p-4 bg-slate-50 border-b">
            <div className="flex flex-wrap gap-4 text-sm">
              {tracking?.store?.phone && (
                <a href={`tel:${tracking.store.phone}`} className="flex items-center gap-2 text-slate-600 hover:text-blue-600">
                  <Phone className="w-4 h-4" />
                  {tracking.store.phone}
                </a>
              )}
              {tracking?.store?.email && (
                <a href={`mailto:${tracking.store.email}`} className="flex items-center gap-2 text-slate-600 hover:text-blue-600">
                  <Mail className="w-4 h-4" />
                  {tracking.store.email}
                </a>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Delivery Status */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Delivery Status</CardTitle>
              {getStatusBadge(tracking?.hand_off_pending && !['completed', 'failed', 'skipped'].includes(tracking?.status) ? 'hand_off_pending' : tracking?.status)}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Date and ETA */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-xs text-slate-500 mb-1">Delivery Date</p>
                <p className="font-semibold text-slate-800">{formatDate(tracking?.delivery_date) || tracking?.delivery_date}</p>
              </div>
              {etaDisplay ? (
                <div className="bg-blue-50 rounded-lg p-3">
                  <p className="text-xs text-blue-600 mb-1 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    Estimated Arrival
                  </p>
                  <p className="font-semibold text-blue-800">{etaDisplay}</p>
                </div>
              ) : tracking?.courier ? null : tracking?.hand_off_pending ? (
                /* v5.62 r31: reserved for a courier, not yet confirmed — neither the in-house
                   route nor the courier is claimed; say so */
                <div className="bg-sky-50 rounded-lg p-3" data-testid="hand-off-pending">
                  <p className="text-xs text-sky-700 mb-1">Estimated Arrival</p>
                  <p className="text-sm text-sky-800">Being handed to a courier — details will appear here shortly</p>
                </div>
              ) : (
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs text-slate-500 mb-1">Estimated Arrival</p>
                  <p className="text-sm text-slate-600">Will be available when route starts</p>
                </div>
              )}
            </div>

            {/* Stop Number */}
            {tracking?.delivery?.stop_number && (
              <div className="bg-amber-50 rounded-lg p-3 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <span className="text-amber-700 font-bold">#{tracking.delivery.stop_number}</span>
                </div>
                <div>
                  <p className="text-sm text-amber-800 font-medium">Stop #{tracking.delivery.stop_number} on today's route</p>
                  <p className="text-xs text-amber-600">Your driver will arrive in order</p>
                </div>
              </div>
            )}

            {/* Delivery Address */}
            {tracking?.delivery?.address && (
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <MapPin className="w-5 h-5 text-slate-400 mt-0.5" />
                <div>
                  <p className="text-xs text-slate-500 mb-1">Delivery Address</p>
                  <p className="text-slate-800">{tracking.delivery.address}</p>
                  {tracking.delivery.notes && (
                    <p className="text-sm text-slate-500 mt-1">Note: {tracking.delivery.notes}</p>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Driver Location Map */}
        {tracking?.driver && tracking.status !== 'completed' && tracking.status !== 'failed' && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Navigation className="w-5 h-5 text-blue-600" />
                Live Driver Location
              </CardTitle>
            </CardHeader>
            <CardContent>
              <DriverLocationMap 
                driverLocation={tracking.driver.current_location}
                deliveryAddress={tracking.delivery?.address}
                driverName={tracking.driver.name}
              />
            </CardContent>
          </Card>
        )}

        {/* Driver Information */}
        {/* v5.61 — outsourced delivery: the courier's card instead of a driver's */}
        {tracking?.courier && !tracking?.driver && (
          <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm" data-testid="courier-card">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Delivered by</p>
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="font-semibold text-slate-800" data-testid="courier-card-name">{tracking.courier.name}</p>
                <p className="text-sm text-slate-500">
                  {tracking.status === 'completed' ? 'Delivered' : tracking.status === 'failed' ? 'Delivery attempt failed' : tracking.courier.route_started ? 'Out for delivery' : 'Scheduled with the courier'}
                </p>
                {tracking.courier.eta_earliest && tracking.courier.eta_latest && tracking.status !== 'completed' && tracking.status !== 'failed' && (
                  <p className="text-sm text-slate-600 mt-1" data-testid="courier-card-eta">Arriving between {formatTime(tracking.courier.eta_earliest)} and {formatTime(tracking.courier.eta_latest)}</p>
                )}
              </div>
              {tracking.courier.tracking_url && (
                <a href={tracking.courier.tracking_url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline whitespace-nowrap" data-testid="courier-card-link">
                  Track with {tracking.courier.name}
                </a>
              )}
            </div>
          </div>
        )}

        {tracking?.driver && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Truck className="w-5 h-5 text-blue-600" />
                Your Driver
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <User className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="font-semibold text-slate-800">{tracking.driver.name}</p>
                  {tracking.driver.phone && (
                    <a href={`tel:${tracking.driver.phone}`} className="text-sm text-blue-600 hover:underline flex items-center gap-1">
                      <Phone className="w-3 h-3" />
                      {tracking.driver.phone}
                    </a>
                  )}
                </div>
              </div>
              
              {/* Driver's last update time */}
              {tracking.driver.last_location_update && (
                <p className="text-xs text-slate-500">
                  Last location update: {formatDateTime(tracking.driver.last_location_update)}
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Package Information */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Package className="w-5 h-5 text-green-600" />
              Package Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            {tracking?.package?.customer_name && (
              <p className="text-slate-800 font-medium mb-3">{tracking.package.customer_name}</p>
            )}
            
            {tracking?.package?.items && tracking.package.items.length > 0 ? (
              <div className="space-y-1">
                <p className="text-xs text-slate-500 mb-2">Items in your delivery:</p>
                <ul className="space-y-2">
                  {tracking.package.items.map((item, idx) => {
                    // Handle different item formats
                    let itemName = '';
                    let itemQty = '';
                    
                    if (typeof item === 'string') {
                      itemName = item;
                    } else if (item.name) {
                      itemName = item.name;
                      itemQty = item.quantity ? ` x${item.quantity}` : '';
                    } else if (item.meal_name) {
                      itemName = item.meal_name;
                      itemQty = item.quantity ? ` x${item.quantity}` : '';
                    } else if (item.item_name) {
                      itemName = item.item_name;
                      itemQty = item.quantity ? ` x${item.quantity}` : '';
                    } else {
                      itemName = JSON.stringify(item);
                    }
                    
                    return (
                      <li key={idx} className="text-sm text-slate-700 flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2">
                        <span className="w-2 h-2 rounded-full bg-green-500"></span>
                        {itemName}{itemQty}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : (
              <div className="text-center py-4">
                <Package className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500">Your meal package is being prepared</p>
                <p className="text-xs text-slate-400 mt-1">Package details will appear here</p>
              </div>
            )}
            
            {tracking?.package?.delivery_notes && (
              <div className="mt-3 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                <p className="text-xs text-yellow-700 font-medium">Delivery Instructions:</p>
                <p className="text-sm text-yellow-800">{tracking.package.delivery_notes}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Proof of Delivery */}
        {tracking?.status === 'completed' && tracking?.proof_of_delivery && (
          <Card className="border-green-200 bg-green-50/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2 text-green-700">
                <CheckCircle2 className="w-5 h-5" />
                Delivery Completed
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-green-700">
                Delivered on {formatDateTime(tracking.proof_of_delivery.completed_at)}
              </p>
              
              {tracking.proof_of_delivery.photo_url && (
                <div className="relative">
                  <p className="text-xs text-slate-500 mb-2 flex items-center gap-1">
                    <ImageIcon className="w-3 h-3" />
                    Proof of Delivery Photo
                  </p>
                  <img 
                    src={tracking.proof_of_delivery.photo_url} 
                    alt="Proof of delivery"
                    className="rounded-lg w-full max-h-64 object-cover border border-green-200"
                  />
                </div>
              )}
              
              {tracking.proof_of_delivery.signature_url && (
                <div>
                  <p className="text-xs text-slate-500 mb-2">Signature</p>
                  <img 
                    src={tracking.proof_of_delivery.signature_url} 
                    alt="Signature"
                    className="rounded-lg bg-white p-2 border border-green-200 max-h-24"
                  />
                </div>
              )}
              
              {tracking.proof_of_delivery.notes && (
                <div className="bg-white rounded-lg p-3 border border-green-200">
                  <p className="text-xs text-slate-500 mb-1">Driver Notes</p>
                  <p className="text-sm text-slate-700">{tracking.proof_of_delivery.notes}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Failed Delivery */}
        {tracking?.status === 'failed' && tracking?.failed_delivery && (
          <Card className="border-red-200 bg-red-50/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2 text-red-700">
                <XCircle className="w-5 h-5" />
                Delivery Failed
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-red-700">
                Attempted on {formatDateTime(tracking.failed_delivery.failed_at)}
              </p>
              
              {tracking.failed_delivery.reason && (
                <div className="bg-white rounded-lg p-3 border border-red-200">
                  <p className="text-xs text-slate-500 mb-1">Reason</p>
                  <p className="text-sm text-red-700 font-medium">{tracking.failed_delivery.reason}</p>
                </div>
              )}
              
              {tracking.failed_delivery.photo_url && (
                <div>
                  <p className="text-xs text-slate-500 mb-2 flex items-center gap-1">
                    <ImageIcon className="w-3 h-3" />
                    Photo
                  </p>
                  <img 
                    src={tracking.failed_delivery.photo_url} 
                    alt="Failed delivery"
                    className="rounded-lg w-full max-h-64 object-cover border border-red-200"
                  />
                </div>
              )}
              
              {tracking.failed_delivery.notes && (
                <div className="bg-white rounded-lg p-3 border border-red-200">
                  <p className="text-xs text-slate-500 mb-1">Notes</p>
                  <p className="text-sm text-slate-700">{tracking.failed_delivery.notes}</p>
                </div>
              )}
              
              {/* Contact store for assistance */}
              <div className="bg-white rounded-lg p-4 border border-red-200 text-center">
                <p className="text-sm text-slate-600 mb-3">Please contact us to reschedule your delivery</p>
                <div className="flex justify-center gap-4">
                  {tracking.store?.phone && (
                    <a 
                      href={`tel:${tracking.store.phone}`}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                    >
                      <Phone className="w-4 h-4" />
                      Call Us
                    </a>
                  )}
                  {tracking.store?.email && (
                    <a 
                      href={`mailto:${tracking.store.email}`}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors"
                    >
                      <Mail className="w-4 h-4" />
                      Email Us
                    </a>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <div className="text-center text-xs text-slate-400 py-4">
          <p>This page updates automatically every 30 seconds</p>
          <p className="mt-1">Tracking ID: {trackingToken?.substring(0, 12)}...</p>
        </div>
      </div>
    </div>
  );
};

export default DeliveryTrackingPage;
